import CorrectIcon from 'assets/svg/correct/feather-check.svg'
import SuccessIcon from 'assets/svg/success-icon/success-icon.svg'
import Flex from 'common/components/Flex'
import Image from 'common/components/Image'
import Typography from 'common/components/Typography'
import theme from 'common/theme'
import isReactElement from 'lib/is-react-element'
import { memo } from 'react'
import styled from 'styled-components'

const Dashed = styled.div`
  position: absolute;
  width: 12px;
  height: 2px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${theme.colors.white};
  border-radius: ${theme.radius.lg};
`
const CheckboxContainer = styled.div`
  display: inline-block;
  height: ${(props) => props.height};
  margin: ${(props) => props.margin};
`

const setBackground = (checked, disabled, variant) => {
  let color = theme.colors.orange
  if (disabled) {
    color = theme.colors.gray
  }
  switch (true) {
    case variant === 'image':
      if (!checked && !disabled) {
        color = theme.colors.white
      } else if (checked && !disabled) {
        color = 'rgb(245, 130, 32, 0.05)'
      }
      break

    default:
      if (!checked && !disabled) {
        color = theme.colors.white
      }
      break
  }
  return `background-color: ${color};`
}

const HiddenCheckbox = styled.input.attrs((props) => ({
  type: 'checkbox',
  id: props.id,
  disabled: props.disabled,
}))`
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  display: none;
  white-space: nowrap;
  width: 1px;
`

const StyledCheckbox = styled.div`
  ${(props) => setBackground(props.checked, props.disabled, '')}
  position: relative;
  border: solid 1px
    ${(props) =>
      props.checked && !props.disabled
        ? `${theme.colors.orange}`
        : `${theme.colors.gray}`};
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  transition: all 150ms;
  &:hover {
    cursor: ${(props) =>
      props.disabled ? 'not-allowed' : 'pointer'};
  }
  svg {
    position: relative;
    bottom: 3px;
    left: 2px;
  }
`

const ImageWrapper = styled.div`
  ${(props) => setBackground(props.checked, props.disabled, 'image')}
  width: ${({ width }) => width || '66px'};
  height: ${({ height }) => height || '66px'};
  padding: ${(props) => props.padding};
  border: solid 1px
    ${(props) =>
      !props.checked || props.disabled
        ? theme.colors.grayLight
        : theme.colors.orange};
  border-radius: ${({ borderRadius }) => borderRadius || '6px'};
  &:hover {
    cursor: ${(props) =>
      props.disabled ? 'not-allowed' : 'pointer'};
  }
  img {
    opacity: ${(props) => (props.disabled ? '0.5' : '1')};
  }
`

const SmallImageContainer = styled.div`
  background-color: ${theme.colors.white};
  border: 2px solid ${theme.colors.grayLighter};
  border-radius: 10px;
  width: fit-content;
  padding: 4px;
  box-sizing: border-box;
`

const IconImage = styled.div`
  position: relative;
  right: 11px;
  bottom: 6px;
  height: 0;
  width: 0;
`
interface ICheckbox {
  checked?: boolean
  onClick: Function
  disabled?: boolean
  id?: string
  required?: boolean
  margin?: string
  value?: any
  padding?: string
  height?: string
  variant?: JSX.Element | string
  imgUrl?: string
  indeterminate?: boolean
  imgDescription?: JSX.Element | string
}

const CheckboxIcon = memo(function CheckboxIcon({
  checked,
  indeterminate,
}: {
  checked: boolean
  indeterminate: boolean
}) {
  if (indeterminate) return <Dashed data-testid="dashed-component" />
  if (checked) return <CorrectIcon data-testid="checked-component" />

  return <></>
})

function Checkbox({
  onClick,
  id,
  disabled,
  margin,
  checked = false,
  required = true,
  padding = '10px',
  height = 'auto',
  value = '',
  variant = '',
  imgUrl = '',
  indeterminate = false,
  imgDescription,
}: ICheckbox): JSX.Element {
  const onClickItem = () => {
    if (!disabled) {
      onClick()
    }
  }
  return (
    <CheckboxContainer margin={margin} height={height}>
      <HiddenCheckbox
        id={id}
        checked={checked}
        onChange={onClickItem}
        required={required}
        disabled={disabled}
        value={value}
      />
      {variant === 'image' && (
        <Flex>
          <ImageWrapper
            onClick={onClickItem}
            padding={padding}
            checked={checked}
            disabled={disabled}
            data-testid="checkbox-image"
          >
            <Image
              src={imgUrl}
              width="66px"
              height="66px"
              objectFit="contain"
            />
          </ImageWrapper>
          {checked && !disabled && (
            <IconImage>
              <SuccessIcon />
            </IconImage>
          )}
        </Flex>
      )}
      {variant === '' && (
        <>
          <StyledCheckbox
            checked={checked}
            onClick={onClickItem}
            disabled={disabled}
            data-testid="checkbox-default"
          >
            <CheckboxIcon
              checked={checked}
              indeterminate={indeterminate}
            />
          </StyledCheckbox>
        </>
      )}

      {variant === 'image-description' && (
        <Flex>
          <ImageWrapper
            onClick={onClickItem}
            padding={padding}
            checked={checked}
            disabled={disabled}
            data-testid="checkbox-image"
            height="fit-content"
            width="202px"
            borderRadius={theme.radius.lg}
          >
            <Flex alignItems="center" gap="4px">
              <SmallImageContainer>
                <Image
                  src={imgUrl}
                  width="41px"
                  height="41px"
                  objectFit="contain"
                />
              </SmallImageContainer>
              {isReactElement(imgDescription) ? (
                <>{imgDescription}</>
              ) : (
                <Typography type="blabel">
                  {imgDescription}
                </Typography>
              )}
            </Flex>
          </ImageWrapper>
          {checked && !disabled && (
            <IconImage>
              <SuccessIcon />
            </IconImage>
          )}
        </Flex>
      )}
    </CheckboxContainer>
  )
}

export default Checkbox
